import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  CheckCircle,
  CreditCard,
  Schedule,
  Euro,
  Star,
  Close,
  Payment,
} from '@mui/icons-material';
import { activateSubscription, SubscriptionActivationRequest } from '../services/api';
import { StoredPlanSelection } from '../utils/planSelection';
import { trackEvent, trackUserAction } from '../utils/analytics';

interface PlanActivationDialogProps {
  open: boolean;
  onClose: () => void;
  planSelection: StoredPlanSelection;
  onActivationSuccess?: () => void;
}

const PlanActivationDialog: React.FC<PlanActivationDialogProps> = ({
  open,
  onClose,
  planSelection,
  onActivationSuccess,
}) => {
  const [activating, setActivating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { plan } = planSelection;

  // Track when dialog opens
  React.useEffect(() => {
    if (open) {
      trackEvent('plan_activation_dialog_opened', 'pricing', plan.display_name, plan.price);
      trackUserAction('plan_activation_dialog_viewed', {
        planId: plan.id,
        planName: plan.name,
        planDisplayName: plan.display_name,
        price: plan.price,
        billingCycle: plan.billing_cycle,
        activityLimit: plan.activity_limit,
        source: planSelection.source,
        timestamp: new Date().toISOString()
      });
      
      console.log('📊 Analytics: Plan activation dialog opened -', {
        plan: plan.display_name,
        price: plan.price,
        source: planSelection.source
      });
    }
  }, [open, plan, planSelection.source]);

  const handleActivate = async () => {
    setActivating(true);
    setError(null);

    // Track activation attempt
    trackEvent('plan_activation_attempt', 'pricing', plan.display_name, plan.price);
    trackUserAction('plan_activation_initiated', {
      planId: plan.id,
      planName: plan.name,
      planDisplayName: plan.display_name,
      price: plan.price,
      billingCycle: plan.billing_cycle,
      activityLimit: plan.activity_limit,
      paymentMethod: 'credit_card',
      source: planSelection.source,
      timestamp: new Date().toISOString()
    });

    try {
      // In a real implementation, you would integrate with a payment processor like Stripe
      // For now, we'll simulate the payment process
      const mockPaymentReference = `sim_pi_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      const subscriptionData: SubscriptionActivationRequest = {
        plan_name: plan.name,
        payment_reference: mockPaymentReference,
        payment_method: 'credit_card',
      };

      console.log('💳 Simulating plan activation for:', plan.display_name);
      
      // Simulate payment processing delay
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const result = await activateSubscription(subscriptionData);
      
      // Track successful activation
      trackEvent('plan_activation_success', 'pricing', plan.display_name, plan.price);
      trackUserAction('plan_activation_completed', {
        planId: plan.id,
        planName: plan.name,
        planDisplayName: plan.display_name,
        price: plan.price,
        billingCycle: plan.billing_cycle,
        activityLimit: plan.activity_limit,
        paymentReference: mockPaymentReference,
        activatedPlan: result.subscription.plan_display_name,
        startDate: result.subscription.start_date,
        endDate: result.subscription.end_date,
        amountPaid: result.subscription.amount_paid,
        source: planSelection.source,
        processingTimeSeconds: 2 // simulation time
      });
      
      console.log('✅ Plan activated successfully:', result.subscription.plan_display_name);
      console.log('📊 Analytics: Plan activation successful -', {
        plan: plan.display_name,
        amountPaid: result.subscription.amount_paid,
        billingCycle: plan.billing_cycle
      });
      
      if (onActivationSuccess) {
        onActivationSuccess();
      }
      
      onClose();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to activate plan';
      
      // Track failed activation
      trackEvent('plan_activation_failure', 'pricing', errorMessage, plan.price);
      trackUserAction('plan_activation_failed', {
        planId: plan.id,
        planName: plan.name,
        planDisplayName: plan.display_name,
        price: plan.price,
        billingCycle: plan.billing_cycle,
        error: errorMessage,
        source: planSelection.source,
        timestamp: new Date().toISOString()
      });
      
      console.error('❌ Plan activation failed:', err);
      console.log('📊 Analytics: Plan activation failed -', {
        plan: plan.display_name,
        error: errorMessage
      });
      setError(errorMessage);
    } finally {
      setActivating(false);
    }
  };

  const formatPrice = (price: number, billingCycle: string) => {
    return `€${price}/${billingCycle === 'monthly' ? 'month' : 'year'}`;
  };

  const getPlanFeaturesList = (features: string[]) => {
    return features.slice(0, 4); // Show first 4 features
  };

  return (
    <Dialog
      open={open}
      onClose={!activating ? onClose : undefined}
      maxWidth="sm"
      fullWidth
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          backdropFilter: 'blur(4px)',
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(248, 250, 252, 0.95))',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CreditCard color="primary" sx={{ fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Activate {plan.display_name}
            </Typography>
          </Box>
          {!activating && (
            <Button
              onClick={onClose}
              sx={{ minWidth: 'auto', p: 1, borderRadius: '50%' }}
            >
              <Close />
            </Button>
          )}
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Plan Details Card */}
        <Card 
          elevation={2} 
          sx={{ 
            mb: 3,
            background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.1), rgba(76, 175, 80, 0.1))',
            border: '1px solid',
            borderColor: 'primary.main',
          }}
        >
          <CardContent sx={{ pb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {plan.display_name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {plan.description}
                </Typography>
              </Box>
              <Chip
                label="Selected"
                color="primary"
                size="small"
                icon={<Star />}
              />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Euro color="success" />
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                  {formatPrice(plan.price, plan.billing_cycle)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Schedule color="info" />
                <Typography variant="body2" color="text.secondary">
                  {plan.activity_limit} activities
                </Typography>
              </Box>
            </Box>

            {/* Features List */}
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              What's included:
            </Typography>
            <List dense sx={{ py: 0 }}>
              {getPlanFeaturesList(plan.features).map((feature, index) => (
                <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <CheckCircle color="success" sx={{ fontSize: 18 }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={feature}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>

        <Divider sx={{ my: 3 }} />

        {/* Payment Information */}
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <Payment color="action" sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
            Ready to Activate?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Your plan will be activated immediately and you can start creating activities right away.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            * This is a simulation - no actual payment will be processed
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button
          onClick={() => {
            // Track activation cancellation
            trackEvent('plan_activation_cancelled', 'pricing', plan.display_name);
            trackUserAction('plan_activation_cancelled', {
              planId: plan.id,
              planName: plan.name,
              planDisplayName: plan.display_name,
              price: plan.price,
              source: planSelection.source
            });
            console.log('📊 Analytics: Plan activation cancelled -', { plan: plan.display_name });
            onClose();
          }}
          disabled={activating}
          sx={{ mr: 1 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleActivate}
          disabled={activating}
          startIcon={activating ? <CircularProgress size={16} /> : <CreditCard />}
          sx={{ minWidth: 140 }}
        >
          {activating ? 'Activating...' : `Activate Plan`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PlanActivationDialog;